"use client";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { CheckIcon, MinusIcon, SearchIcon } from "lucide-react";
import React from "react";
import { Input } from "@/components/ui/input";
import {
  TooltipContent,
  TooltipTrigger,
  TruncateTooltip,
} from "@/components/ui/truncate-tooltip";
import { cn } from "@/lib/utils";
import type { UserAnalyticsViewPresetID } from "../_utils/preset-config";

// shadcn/ui Checkboxと同じスタイルのカスタムコンポーネント
const ShadcnCheckbox = React.memo(
  ({
    checked,
    indeterminate = false,
    className,
    ...props
  }: {
    checked: boolean;
    indeterminate?: boolean;
    className?: string;
  }) => {
    return (
      <div
        className={cn(
          "group peer border-input dark:bg-input/30",
          checked &&
            "bg-primary text-primary-foreground dark:bg-primary border-primary",
          indeterminate &&
            "bg-primary text-primary-foreground dark:bg-primary border-primary",
          "size-3.5 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
          "flex items-center justify-center",
          className,
        )}
        {...props}
      >
        {checked && !indeterminate && (
          <CheckIcon className="size-3 text-current" />
        )}
        {indeterminate && <MinusIcon className="size-3 text-current" />}
      </div>
    );
  },
);
ShadcnCheckbox.displayName = "ShadcnCheckbox";

interface PresetCardsProps {
  presets: Array<{
    presetID: UserAnalyticsViewPresetID;
    name: string;
    description?: string;
    categoryKey: string;
    categoryLabel: string;
    categoryColor: string;
  }>;
  selectedPresetIds: UserAnalyticsViewPresetID[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onPresetToggle: (presetId: UserAnalyticsViewPresetID) => void;
}

export const PresetCards = React.memo(function PresetCards({
  presets,
  selectedPresetIds,
  searchQuery,
  onSearchChange,
  onPresetToggle,
}: PresetCardsProps) {
  const { i18n } = useLingui();

  return (
    <div className="h-full overflow-hidden flex flex-col border border-l-0 rounded-r-lg">
      <div className="h-[46px] px-3 border-b bg-muted/50 flex items-center flex-shrink-0">
        <div className="flex items-center justify-between w-full">
          <span className="text-xs font-semibold text-foreground">
            <Trans>Create Views from Presets</Trans>
          </span>
          {selectedPresetIds.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {selectedPresetIds.length} <Trans>selected</Trans>
            </span>
          )}
        </div>
      </div>

      {/* 検索バー */}
      <div className="p-3 border-b">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={i18n._(msg`Search presets...`)}
            className="pl-9 h-8 text-xs"
          />
        </div>
      </div>

      {/* プリセット一覧 - 2カラムレイアウト */}
      <div className="flex-1 overflow-y-auto p-3">
        {presets.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-8">
            <Trans>No presets found</Trans>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {presets.map((preset) => (
              <div
                key={preset.presetID}
                className="relative flex items-start space-x-2.5 p-3 border rounded-lg hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={() => onPresetToggle(preset.presetID)}
              >
                <div className="pt-[2px]">
                  <ShadcnCheckbox
                    checked={selectedPresetIds.includes(preset.presetID)}
                    className="h-3.5 w-3.5"
                  />
                </div>
                <div className="flex-1 min-w-0 pb-4">
                  <div className="font-semibold text-[13px] line-clamp-1 text-foreground">
                    {preset.name}
                  </div>
                  {preset.description && (
                    <TruncateTooltip>
                      <TooltipTrigger asChild>
                        <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5 leading-relaxed text-left">
                          {preset.description}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-sm">{preset.description}</p>
                      </TooltipContent>
                    </TruncateTooltip>
                  )}
                </div>
                <span
                  className={`absolute bottom-1.5 right-1.5 text-[10px] px-1 py-0.5 rounded font-medium ${preset.categoryColor}`}
                >
                  {preset.categoryLabel}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
